import {useCallback, useEffect, useState} from "react";
import {
    executeHubThingActions,
    findHubById,
    findHubThings,
    updateHub,
    updateHubThing,
    updateThing
} from "../api/RagnarokApi";
import {useSnackbarContext} from "../components/Snackbar";

export default function RagnarokHub(hubId) {
    const [hub, setHub] = useState(undefined);
    const [hubThings, setHubThings] = useState(undefined);
    const [hubLoading, setHubLoading] = useState(false);
    const [actuatorValidationMap, setActuatorValidationMap] = useState(undefined);

    const {handleMessageUpdate} = useSnackbarContext();
    const handleErrorCallback = useCallback(handleError, []);

    useEffect(() => {
        handleHubLoadingChange(true);

        findHubById(hubId)
            .then((resp) => resp.json())
            .then((hubData) =>
                findHubThings(hubId)
                    .then((resp) => resp.json())
                    .then((thingsData) => {
                        handleHubChange(hubData.result);
                        handleHubThingsChange(
                            thingsData.result.map(thing => {
                                switch (thing["manufacturer"]) {
                                    case "SORENSEN_XG": {
                                        return Object.assign(thing, {
                                            "actuator_configuration": Object.assign({
                                                "address": 0,
                                                "current_set_point": 0,
                                                "voltage_set_point": 0,
                                                "main_output_control": "OFF"
                                            }, thing["actuator_configuration"])
                                        });
                                    }
                                    case "SPELLMAN_ICA": {
                                        return Object.assign(thing, {
                                            "actuator_configuration": Object.assign({
                                                "heater_grid_enables": false,
                                                "arc_fault_program": 0,
                                                "mean_cathode_over_current": 0,
                                                "grid_pulsed_voltage": 0,
                                                "cathode_voltage": 0,
                                                "heater_voltage": 0
                                            }, thing["actuator_configuration"])
                                        });
                                    }
                                    case "PEAK_COM_ARU2856C": {
                                        return Object.assign(thing, {
                                            "actuator_configuration": Object.assign({
                                                "power_level": 0,
                                                "override_rf_inhibit_status": false,
                                                "frequency": 0,
                                                "trim": 0
                                            }, thing["actuator_configuration"])
                                        });
                                    }
                                    default:
                                        return thing;
                                }
                            })
                        );
                        handleActuatorValidationMapChange(
                            thingsData.result.reduce((result, thing) => {
                                switch (thing["manufacturer"]) {
                                    case "SORENSEN_XG": {
                                        result[thing["id"]] = {
                                            "address": false,
                                            "current_set_point": false,
                                            "voltage_set_point": false
                                        };
                                        return result;
                                    }
                                    case "SPELLMAN_ICA": {
                                        result[thing["id"]] = {
                                            "arc_fault_program": false,
                                            "mean_cathode_over_current": false,
                                            "grid_pulsed_voltage": false,
                                            "cathode_voltage": false,
                                            "heater_voltage": false
                                        };
                                        return result;
                                    }
                                    case "PEAK_COM_ARU2856C": {
                                        result[thing["id"]] = {
                                            "power_level": false,
                                            "override_rf_inhibit_status": false,
                                            "frequency": false,
                                            "trim": false
                                        };
                                        return result;
                                    }
                                    default:
                                        result[thing["id"]] = {};
                                        return result;
                                }
                            }, {})
                        );
                        handleHubLoadingChange(false);
                    })
            )
            .catch((res) => {
                handleHubLoadingChange(false);
                handleErrorCallback(res);
            });
    }, [hubId, handleErrorCallback]);

    function handleError(error) {
        handleMessageUpdate((error.error && error.error.message) || error.message);
    }

    function handleHubChange(hub) {
        setHub(hub);
    }

    function handleHubThingsChange(things) {
        setHubThings(things);
    }

    function handleHubLoadingChange(loading) {
        setHubLoading(loading);
    }

    function handleActuatorValidationMapChange(map) {
        setActuatorValidationMap(map);
    }

    function handleHubUpdate(launched) {
        return updateHub(hub.id, launched)
            .then(res => res.json())
            .then(() => {
                handleHubChange(Object.assign({}, hub, {launched}));
            }).catch(res => {
                handleError(res);
            });
    }

    function handleHubThingEnableUpdate(thingId, enabled) {
        return updateHubThing(hub.id, thingId, enabled)
            .then(res => res.json())
            .then(() => {
                const things = [...hubThings];
                const [thing] = things.filter(t => t.id === thingId);

                thing.enabled = enabled;

                handleHubThingsChange(things);
            }).catch(res => {
                handleError(res);
            });
    }

    function handleHubThingCollectionIntervalUpdate(thingId, collectionInterval) {
        return updateHubThing(hub.id, thingId, undefined, collectionInterval)
            .then(res => res.json())
            .then(() => {
                const things = [...hubThings];
                const [thing] = things.filter(t => t.id === thingId);

                thing.collection_interval = collectionInterval;

                handleHubThingsChange(things);
            }).catch(res => {
                handleError(res);
            });
    }

    function handleHubThingActuatorConfigurationChange(thingId, key, value) {
        const things = [...hubThings];
        const [thing] = things.filter(t => t.id === thingId);

        thing.actuator_configuration[key] = value;

        handleHubThingsChange(things);
    }

    function handleHubThingActuatorConfigurationUpdate(thingId) {
        const [thing] = hubThings.filter(t => t.id === thingId);

        return updateHubThing(
            hub.id,
            thingId,
            undefined,
            undefined,
            thing.actuator_configuration
        ).catch(res => {
            handleError(res);
        });
    }

    function handleHubThingCollectionConfigurationChange(thingId, value) {
        const things = [...hubThings];
        const [thing] = things.filter(t => t.id === thingId);

        thing.collection_configuration = Object.assign(thing.collection_configuration, value);

        handleHubThingsChange(things);
    }

    function handleHubThingCollectionConfigurationUpdate(thingId) {
        const [thing] = hubThings.filter(t => t.id === thingId);

        return updateHubThing(
            hub.id,
            thingId,
            undefined,
            undefined,
            undefined,
            thing.collection_configuration
        ).catch(res => {
            handleError(res);
        });
    }

    function handleAddToValidationActuatorMap(thingId, key) {
        const map = Object.assign(actuatorValidationMap);

        map[thingId][key] = true;

        handleActuatorValidationMapChange(map);
    }

    function handleRemoveFromValidationActuatorMap(thingId, key) {
        const map = Object.assign(actuatorValidationMap);

        map[thingId][key] = false;

        handleActuatorValidationMapChange(map);
    }

    function handleHubThingConnectionConfigurationChange(thingId, value) {
        const things = [...hubThings];
        const [thing] = things.filter(t => t.id === thingId);

        thing.connection_configuration = Object.assign(thing.connection_configuration, value);

        handleHubThingsChange(things);
    }

    function handleHubThingConnectionConfigurationUpdate(thingId) {
        const [thing] = hubThings.filter(t => t.id === thingId);

        return updateThing(
            thingId, thing.connection_protocol, thing.connection_configuration
        ).catch(res => {
            handleError(res);
        });
    }

    function handleHubThingExecuteAction(thingId, methods) {
        return executeHubThingActions(hub.id, thingId, methods)
            .catch(res => {
                handleError(res);
            });
    }

    return {
        hub,
        hubThings,
        hubLoading,
        actuatorValidationMap,
        handleHubUpdate,
        handleHubThingEnableUpdate,
        handleHubThingCollectionIntervalUpdate,
        handleHubThingActuatorConfigurationChange,
        handleHubThingActuatorConfigurationUpdate,
        handleAddToValidationActuatorMap,
        handleRemoveFromValidationActuatorMap,
        handleHubThingConnectionConfigurationChange,
        handleHubThingConnectionConfigurationUpdate,
        handleHubThingCollectionConfigurationChange,
        handleHubThingCollectionConfigurationUpdate,
        handleHubThingExecuteAction,
    };
}